/**
 * Значение с подписчиками — для `useSyncExternalStore`: то, что меняется чаще остального состояния (уровень
 * микрофона, кто говорит, набираемый текст), читают только те, кому оно нужно, а не все потребители контекста.
 */
export interface IValueStore<T> {
    get: () => T;
    /** Новое значение; равное прежнему (`equals`) подписчиков не будит */
    set: (next: T) => void;
    subscribe: (listener: () => void) => () => void;
}

/** Стор на чтение — то, что отдают потребителям, когда писать в него может только владелец. */
export type IReadonlyValueStore<T> = Pick<IValueStore<T>, 'get' | 'subscribe'>;

/**
 * @param equals та же величина — подписчиков не будить
 * @param keep что хранить вместо пришедшего значения (копия, общий пустой массив)
 */
export function createValueStore<T>(
    initial: T,
    equals: (a: T, b: T) => boolean = Object.is,
    keep?: (next: T) => T,
): IValueStore<T> {
    let value = initial;
    const listeners = new Set<() => void>();
    return {
        get: () => value,
        set: (next) => {
            if (equals(value, next)) return;
            value = keep ? keep(next) : next;
            listeners.forEach((listener) => listener());
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}

/** Поток событий без значения (курсоры, объявления диктору): подписчики получают каждое. */
export interface IEmitter<T> {
    emit: (event: T) => void;
    subscribe: (listener: (event: T) => void) => () => void;
}

export function createEmitter<T>(): IEmitter<T> {
    const listeners = new Set<(event: T) => void>();
    return {
        emit: (event) => listeners.forEach((listener) => listener(event)),
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}

/** Массивы равны поэлементно: `set` той же выборки подписчиков не будит. */
export const sameItems = <T>(a: readonly T[], b: readonly T[]) =>
    a.length === b.length && a.every((item, index) => Object.is(item, b[index]));
